import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { byPassErrorMessage } from '../core-interceptor/core-interceptor.service';

@Injectable({
  providedIn: 'root'
})

export class CoreHttpService {

  // Authentication is an HttpOnly session cookie set by the API; the cookie and the
  // CSRF header are added by CredentialsInterceptor, so no token is handled here.
  constructor(
    private http: HttpClient,
    private router: Router,
    public spinner: NgxSpinnerService) {
  }

  protected get<T>(method: string, params?: any): Observable<any> {
    return this.http.get<T>(method, {
      headers: this.setHeaders(),
      params: this.setParams(params)
    }).pipe(
      map((response) => this.displayMessages(response)),
      catchError(err => this.handleError(err))
    );
  }

  protected post<T>(method: string, body: any): Observable<any> {
    const model = JSON.stringify(body);
    return this.http.post<T>(method, model,
      { headers: this.setHeaders() }).pipe(
        map(response => this.displayMessages(response)),
        catchError(err => this.handleError(err)));
  }

  protected put<T>(method: string, body: any): Observable<any> {
    const model = JSON.stringify(body);
    return this.http.put<T>(method, model, { headers: this.setHeaders() }).pipe(
      map((response) => this.displayMessages(response)),
      catchError(err => this.handleError(err)));
  }

  protected delete<T>(method: string, params?: any): Observable<any> {
    return this.http.delete<T>(method, { headers: this.setHeaders(), params: this.setParams(params) }).pipe(
      map((response) => this.displayMessages(response)),
      catchError(err => this.handleError(err)));
  }

  protected postFormData(method: string, body: any): Observable<any> {
    return this.http.post(method, body, { headers: this.setHeaders('formData') }).pipe(
      map((response) => this.displayMessages(response)),
      catchError(err => this.handleError(err)));
  }

  protected postByPassErrorMessage(method: string, body: any): Observable<any> {
    return this.http.post(method, body, { headers: this.setHeaders('formData'), context: new HttpContext().set(byPassErrorMessage, true) }).pipe(
      map((response) => this.displayMessages(response)),
      catchError(err => this.handleError(err)));
  }
  
  protected postParams<T>(method: string, body: any, params?: any): Observable<any> {
    return this.http.post<T>(method, "", { params: params, headers: this.setHeaders() }).pipe(
      map(response => this.displayMessages(response)),
      catchError(err => this.handleError(err)));
  }

  /**
   * set appropriate headers and return is
   * @param contentType optional content type header value, default is application/json
   * @returns HttpHeaders with the Content-Type header set
   */
  private setHeaders(contentType?: string) {
    let header = new HttpHeaders();
    if (contentType === 'formData') {
      
    }
    else if (contentType) {
      header = header.set('Content-Type', contentType);
    }
    else {
      header = header.set('Content-Type', 'application/json');
    }
    return header;
  }

  /**
   * set specified params list to HttpParams object
   * @param params list of parameters to set
   * @returns HttpParams object with parameters set
   */
  private setParams(params: any) {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    for (const param of Object.keys(params)) {
      if (params[param]) {
        httpParams = httpParams.set(param, params[param]);
      }
    }

    return httpParams;
  }

  /**
   * display info toastr for each message in response object
   * @param response Api response
   * @returns response as it is
   */
  private displayMessages(response: any) {
    if (response && response.messages && response.messages.length > 0) {
      response.messages.forEach((message: any) => {
        //this.toastrService.info(message.messageText, message.title, { timeOut: 7000 });
      });
    }

    return response;
  }

  /**
   * common error handler
   * 1. for unauthorized error redirect to login page
   * 2. for other errors displat error toastr for each error messages
   * 3. for unknown errro display generic error message
   * @param response Api response
   * @returns observable of null
   */
  private handleError(response: Response | any) {
    let error: any = response.error;

    // Session cookie missing or expired: drop the cached profile and go to login.
    if (response.status == 401) {
      console.log('Your session has ended. Please sign in again.');
      localStorage.removeItem('cereus-token');
      localStorage.removeItem('cereus-cache');
      this.router.navigate(['/']).then();
    }
    else if (response.status == 403) {
      console.log('You are not authorized to access this route.');
      this.router.navigate(['/']).then();
    }
    else if (error && error.errors && error.errors.length > 0) {
      error.errors.forEach((err: any) => {
        console.log('Error-01:', err.errorMessage);
      });
    }
    else {
      console.log('Oops...Something went wrong, please try again or contact support');
    }

    return of(null);
  }

  protected getBlobData<T>(method: string): Observable<any> {
    return this.http.get(method, { headers: this.setHeaders('formData'), responseType: 'blob' }).pipe(
      map((response) => { return response; })
      , catchError(err => this.handleError(err)));
  }
}
